import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, Sparkles, MapPin, Phone, MessageSquare,
  Star, Globe, ExternalLink, Clock, CheckCircle2, AlertCircle,
  Calendar, Users, FileText, Briefcase, Building2, CalendarPlus,
  Package, Save, Plus, Trash2, Printer, Heart, Download,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import HidroFeedbackBar from "../components/HidroFeedbackBar";
import { printInLightMode } from "../lib/printInLightMode";
import { createMap, tealPin, L } from "../lib/leafletMap";
import { BACKEND_URL } from "../lib/api";

// Parse markdown-lite — we only need to handle `#` headings + paragraphs.
// Tiny inline-markdown renderer — handles **bold**, *italic*, `code`, and
// [label](url) links. Avoids pulling react-markdown for a few patterns.
function renderInline(text) {
  if (!text) return null;
  const parts = [];
  // Tokenise on the patterns we care about. Order matters: links first so
  // inner ** doesn't bleed.
  const re = /(\[([^\]]+)\]\(([^)]+)\))|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1]) {
      // [label](url)
      const label = m[2];
      const href = m[3];
      const isInternal = href.startsWith("/");
      parts.push(
        isInternal
          ? <Link key={`l${key++}`} to={href} className="text-[#1fb6c1] hover:text-[#3ad1dc] underline underline-offset-2">{label}</Link>
          : <a key={`l${key++}`} href={href} target="_blank" rel="noreferrer noopener" className="text-[#1fb6c1] hover:text-[#3ad1dc] underline underline-offset-2">{label}</a>
      );
    } else if (m[4]) {
      parts.push(<strong key={`b${key++}`} className="font-bold text-white">{m[5]}</strong>);
    } else if (m[6]) {
      parts.push(<em key={`i${key++}`} className="italic text-[#dde3ec]">{m[7]}</em>);
    } else if (m[8]) {
      parts.push(<code key={`c${key++}`} className="px-1 py-0.5 rounded bg-white/10 text-[#7adde5] text-[0.85em] font-mono">{m[9]}</code>);
    }
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function renderBrief(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const blocks = [];
  let currentSection = null;
  let buffer = [];
  let listBuffer = [];
  const flushPara = () => {
    if (buffer.length) {
      blocks.push({ type: "para", text: buffer.join(" ").trim() });
      buffer = [];
    }
  };
  const flushList = () => {
    if (listBuffer.length) {
      blocks.push({ type: "list", items: [...listBuffer] });
      listBuffer = [];
    }
  };
  for (const line of lines) {
    if (/^#+\s/.test(line)) {
      flushPara();
      flushList();
      if (currentSection) blocks.push({ type: "section-end" });
      const title = line.replace(/^#+\s/, "").trim();
      blocks.push({ type: "section-start", title });
      currentSection = title;
    } else if (/^\s*[-*]\s+/.test(line)) {
      flushPara();
      listBuffer.push(line.replace(/^\s*[-*]\s+/, "").trim());
    } else if (line.trim() === "") {
      flushPara();
      flushList();
    } else {
      flushList();
      buffer.push(line);
    }
  }
  flushPara();
  flushList();
  if (currentSection) blocks.push({ type: "section-end" });

  const ICONS = {
    "1. Overview": Building2,
    "2. What They're Like": Users,
    "3. Who to Talk To": Users,
    "4. Strategic Angle": Sparkles,
    "4. Deal Angle": Sparkles,
    "5. Likely Objections & Counters": AlertCircle,
    "6. Things to Prepare / Bring": Package,
    "7. Key Contacts to Ask For": Users,
    "8. Coach's note to the rep": Heart,
  };
  const COLORS = {
    "1. Overview": "#1FB6C1",
    "2. What They're Like": "#A78BFA",
    "3. Who to Talk To": "#10B981",
    "4. Strategic Angle": "#F59E0B",
    "4. Deal Angle": "#F59E0B",
    "5. Likely Objections & Counters": "#EC4899",
    "6. Things to Prepare / Bring": "#A78BFA",
    "7. Key Contacts to Ask For": "#10B981",
    "8. Coach's note to the rep": "#EF4444",
  };

  const rendered = [];
  let idx = 0;
  while (idx < blocks.length) {
    const b = blocks[idx];
    if (b.type === "section-start") {
      const title = b.title;
      const blocksInSection = [];
      idx++;
      while (idx < blocks.length && blocks[idx].type !== "section-end") {
        if (blocks[idx].type === "para" || blocks[idx].type === "list") {
          blocksInSection.push(blocks[idx]);
        }
        idx++;
      }
      const Icon = ICONS[title] || Sparkles;
      const color = COLORS[title] || "#1FB6C1";
      rendered.push(
        <div key={rendered.length} className="hv-card p-5" data-testid={`brief-section-${title.replace(/\s+/g, "-").toLowerCase()}`}>
          <div className="flex items-center gap-2 mb-3">
            <Icon className="w-5 h-5" style={{ color }} />
            <h3 className="font-display text-lg font-bold" style={{ color }}>{title}</h3>
          </div>
          <div className="space-y-3 text-sm text-[#dde3ec] leading-relaxed">
            {blocksInSection.map((bb, i) => {
              if (bb.type === "list") {
                return (
                  <ul key={i} className="space-y-1.5 list-disc pl-5 marker:text-[#1fb6c1]/60">
                    {bb.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
                  </ul>
                );
              }
              return <p key={i}>{renderInline(bb.text)}</p>;
            })}
          </div>
        </div>
      );
    } else {
      idx++;
    }
  }
  return rendered;
}

function StarBar({ rating, count }) {
  if (!rating) return null;
  return (
    <div className="flex items-center gap-1">
      {[1,2,3,4,5].map((i) => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= Math.round(rating) ? "text-amber-300 fill-amber-300" : "text-white/15"}`} />
      ))}
      <span className="text-xs text-[#a0aabf] ml-1 font-mono">{rating.toFixed(1)} ({count || 0})</span>
    </div>
  );
}

function PriceChip({ level }) {
  if (!level) return null;
  const LEVEL_LABEL = {
    PRICE_LEVEL_FREE: "Free",
    PRICE_LEVEL_INEXPENSIVE: "$",
    PRICE_LEVEL_MODERATE: "$$",
    PRICE_LEVEL_EXPENSIVE: "$$$",
    PRICE_LEVEL_VERY_EXPENSIVE: "$$$$",
  };
  return (
    <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border border-emerald-400/40 bg-emerald-400/10 text-emerald-300">
      {LEVEL_LABEL[level] || level}
    </span>
  );
}

export default function ResearchLead() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromParam = searchParams.get("from") || "";
  // Resolve breadcrumb back-link
  const backTarget = fromParam.startsWith("trip/") || fromParam.startsWith("trips/") ? `/${fromParam}`
    : fromParam.startsWith("customer/") || fromParam.startsWith("customers/") ? `/${fromParam}`
    : fromParam === "sales-funnel" ? "/sales-funnel"
    : fromParam === "leads" ? "/leads"
    : `/leads/${id}`;
  const backLabel = fromParam.startsWith("trip") ? "Back to trip"
    : fromParam.startsWith("customer") ? "Back to customer"
    : fromParam === "sales-funnel" ? "Back to Sales Funnel"
    : fromParam === "leads" ? "Back to Leads"
    : "Back to lead";

  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [savingDoc, setSavingDoc] = useState(false);
  const [savedDocId, setSavedDocId] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [err, setErr] = useState("");
  const [newCheck, setNewCheck] = useState("");

  const loadExisting = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/research/prospect/${id}`);
      setBrief(data);
    } catch (_e) {
      setBrief(null);
    }
    setLoading(false);
  };

  const generate = async (force = false) => {
    setGenerating(true);
    setErr("");
    try {
      const { data } = await api.post(`/research/prospect/${id}`, null, {
        params: force ? { force: true } : {},
      });
      setBrief(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to generate brief");
    }
    setGenerating(false);
  };

  useEffect(() => { loadExisting(); }, [id]);

  const patchBrief = async (patch) => {
    setBrief((b) => b ? { ...b, ...patch } : b);
    try {
      await api.patch(`/research/prospect/${id}`, patch);
    } catch (e) {
      // Iter 290j — surface silent failures. Previously this catch
      // swallowed errors, leaving the optimistic UI update intact
      // while the server hadn't actually saved. Users would lose
      // work without warning.
      console.warn("patchBrief failed", e);
      toast.error("Couldn't save change — try again.");
    }
  };
  const addCheck = () => {
    const t = newCheck.trim();
    if (!t) return;
    const cid = (window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2));
    const next = [...(brief.checklist || []), { id: cid, text: t, done: false }];
    setNewCheck("");
    patchBrief({ checklist: next });
  };
  const toggleCheck = (cid) => {
    const next = (brief.checklist || []).map((c) => c.id === cid ? { ...c, done: !c.done } : c);
    patchBrief({ checklist: next });
  };
  const removeCheck = (cid) => {
    const next = (brief.checklist || []).filter((c) => c.id !== cid);
    patchBrief({ checklist: next });
  };
  const saveToCustomerDocs = async () => {
    setSavingDoc(true); setErr("");
    try {
      const { data } = await api.post(`/research/save-to-customer-docs/${id}`);
      setSavedDocId(data.doc_id);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save");
    }
    setSavingDoc(false);
  };

  if (loading) return <div className="text-center py-12 text-xs text-[#647087]">Loading…</div>;

  if (!brief) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16 space-y-4" data-testid="research-empty">
        <Sparkles className="w-10 h-10 text-[#1fb6c1] mx-auto" />
        <h1 className="font-display text-2xl font-bold">Deep Sales Brief</h1>
        <p className="text-sm text-[#a0aabf]">
          No brief exists yet for this lead. Generating will combine Google Places data, the lodge's website, Wayback Machine history, and all your internal call logs + comments + uploaded docs into a 5-section actionable brief. Takes ~15-25 seconds.
        </p>
        {err && <div className="hv-card p-3 text-xs text-rose-300 border-rose-500/40">{err}</div>}
        <div className="flex items-center justify-center gap-2">
          <button type="button" onClick={() => navigate(backTarget)} className="hv-btn hv-btn-ghost" data-testid="research-back">
            <ArrowLeft className="w-4 h-4" /> {backLabel}
          </button>
          <button type="button" onClick={() => generate(false)} disabled={generating} className="hv-btn hv-btn-primary" data-testid="research-generate">
            <Sparkles className="w-4 h-4" /> {generating ? "Researching…" : "Generate deep brief"}
          </button>
        </div>
      </div>
    );
  }

  const p = brief.place || {};
  const contact = brief.lead_contact || {};
  const phone = p.phone || contact.phone || "";
  const website = p.website || contact.website || "";
  const loc = p.location || {};
  const hasCoords = loc.latitude != null && loc.longitude != null;
  const waPhone = phone.replace(/\D/g, "");

  return (
    <div className="space-y-4 pb-12 md:pb-6" data-testid="research-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <button type="button" onClick={() => navigate(backTarget)} className="hv-btn hv-btn-ghost text-xs mb-2" data-testid="research-back">
            <ArrowLeft className="w-4 h-4" /> {backLabel}
          </button>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087] flex items-center gap-2">
            <Sparkles className="w-3 h-3 text-[#1fb6c1]" />
            Deep Sales Brief
            <span className="text-[10px] font-mono">· generated {brief.created_at?.slice(0, 10)}</span>
          </div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1">{brief.prospect_name}</h1>
          <div className="flex items-center gap-3 mt-2 text-xs text-[#a0aabf] flex-wrap">
            <StarBar rating={p.rating} count={p.review_count} />
            <PriceChip level={p.price_level} />
            {brief.wayback_first_seen && (
              <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border border-white/10 flex items-center gap-1">
                <Clock className="w-2.5 h-2.5" />
                Online since {brief.wayback_first_seen}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => generate(true)} disabled={generating} className="hv-btn hv-btn-ghost text-xs" data-testid="research-refresh">
            <RefreshCw className={`w-3 h-3 ${generating ? "animate-spin" : ""}`} /> {generating ? "…" : "Refresh"}
          </button>
        </div>
      </div>

      {err && <div className="hv-card p-3 text-xs text-rose-300 border-rose-500/40">{err}</div>}

      {/* Photo hero */}
      {brief.photos?.length > 0 && (
        <div className="grid grid-cols-4 gap-2 max-h-[280px] overflow-hidden rounded-lg" data-testid="research-photo-hero">
          {brief.photos.slice(0, 4).map((ph, idx) => (
            <div
              key={idx}
              className={`${idx === 0 ? "col-span-2 row-span-2 aspect-square" : "aspect-square"} bg-white/5 overflow-hidden`}
            >
              <img
                src={`${BACKEND_URL}/api/research/photo?name=${encodeURIComponent(ph.name)}&max_width=${idx === 0 ? 1200 : 600}`}
                alt=""
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                loading="lazy"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Quick actions + contact strip */}
      <div className="hv-card p-3 flex items-center gap-2 flex-wrap">
        {phone && (
          <>
            <a href={`tel:${phone}`} className="hv-btn hv-btn-primary text-xs" data-testid="research-call">
              <Phone className="w-3 h-3" /> {phone}
            </a>
            <a href={`https://wa.me/${waPhone}`} target="_blank" rel="noreferrer" className="hv-btn hv-btn-ghost text-xs text-emerald-300" data-testid="research-whatsapp">
              <MessageSquare className="w-3 h-3" /> WhatsApp
            </a>
          </>
        )}
        {website && (
          <a href={website} target="_blank" rel="noreferrer" className="hv-btn hv-btn-ghost text-xs" data-testid="research-website">
            <Globe className="w-3 h-3" /> Website <ExternalLink className="w-2.5 h-2.5 opacity-50" />
          </a>
        )}
        {p.address && (
          <span className="text-xs text-[#a0aabf] flex items-center gap-1 ml-auto">
            <MapPin className="w-3 h-3 text-[#1fb6c1]" /> {p.address}
          </span>
        )}
      </div>

      {/* Two-column: map + editorial summary */}
      <div className="grid md:grid-cols-2 gap-4">
        {hasCoords && (
          <div className="hv-card overflow-hidden h-56" data-testid="research-map">
            <ResearchMiniMap lat={loc.latitude} lng={loc.longitude} label={p.address || brief.prospect_name} />
          </div>
        )}
        {p.editorial_summary && (
          <div className="hv-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-2">Editorial summary</div>
            <p className="text-sm text-[#d4dbe7] leading-relaxed">{p.editorial_summary}</p>
          </div>
        )}
      </div>

      {/* The Brief — 5 sections */}
      <div className="grid md:grid-cols-2 gap-4" data-testid="research-brief-body">
        {renderBrief(brief.main_md || brief.brief_md)}
      </div>

      {/* Hidro feedback bar — helps the playbook learn */}
      <div className="hv-card p-3" data-testid="research-feedback-card">
        <HidroFeedbackBar kind="research" targetId={brief.lead_id || id} />
      </div>

      {/* Things to Prepare / Bring (interactive checklist) */}
      <div className="hv-card p-4" data-testid="research-checklist">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-display text-base font-bold flex items-center gap-2">
            <Package className="w-4 h-4 text-[#A78BFA]" />
            Things to Prepare / Bring
            {(brief.checklist || []).length > 0 && (
              <span className="text-xs font-mono text-[#647087]">
                ({(brief.checklist || []).filter((c) => c.done).length}/{(brief.checklist || []).length})
              </span>
            )}
          </h3>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); addCheck(); }}
          className="flex items-center gap-2 mb-3"
        >
          <input
            value={newCheck}
            onChange={(e) => setNewCheck(e.target.value)}
            placeholder="Add a reminder or item to bring…"
            className="hv-input text-sm flex-1"
            data-testid="research-check-input"
          />
          <button type="submit" className="hv-btn hv-btn-primary text-xs" data-testid="research-check-add">
            <Plus className="w-3 h-3" /> Add
          </button>
        </form>
        {(brief.checklist || []).length === 0 ? (
          <p className="text-xs text-[#647087]">Generate the brief and Hidro will pre-fill smart reminders here. You can also add your own.</p>
        ) : (
          <ul className="space-y-1">
            {brief.checklist.map((it) => (
              <li key={it.id} className="flex items-center gap-2 text-sm" data-testid={`research-check-${it.id}`}>
                <button
                  type="button"
                  onClick={() => toggleCheck(it.id)}
                  className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${it.done ? "bg-[#10B981] border-[#10B981]" : "border-white/30 hover:border-[#1fb6c1]"}`}
                  aria-label={it.done ? "Done" : "Not done"}
                >
                  {it.done && <CheckCircle2 className="w-3 h-3 text-black" />}
                </button>
                <span className={`flex-1 ${it.done ? "line-through text-[#647087]" : "text-[#d4dbe7]"}`}>{it.text}</span>
                <button type="button" onClick={() => removeCheck(it.id)} className="text-[#647087] hover:text-rose-300" aria-label="Remove">
                  <Trash2 className="w-3 h-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Key Contacts to Ask For */}
      {(brief.key_contacts || []).length > 0 && (
        <div className="hv-card p-4" data-testid="research-key-contacts">
          <h3 className="font-display text-base font-bold mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-[#10B981]" />
            Key Contacts to Ask For
          </h3>
          <ul className="space-y-1.5">
            {brief.key_contacts.map((c) => (
              <li key={c.id} className="text-sm flex items-baseline gap-2">
                <span className="text-[10px] uppercase tracking-wider text-[#10B981] font-mono px-1.5 py-0.5 rounded bg-[#10B981]/10 border border-[#10B981]/30 shrink-0">{c.role}</span>
                {c.name && <span className="text-[#d4dbe7]">{c.name}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Reviews — best, worst, summary only (the full list bloated the page) */}
      {brief.reviews?.length > 0 && (() => {
        const sortedByRating = [...brief.reviews].filter((r) => Number.isFinite(r.rating));
        sortedByRating.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        const best = sortedByRating[0] || brief.reviews[0];
        const worst = sortedByRating.length > 1 ? sortedByRating[sortedByRating.length - 1] : null;
        // Average rating + sentiment hint (best-effort).
        const ratings = brief.reviews.map((r) => r.rating).filter(Number.isFinite);
        const avg = ratings.length ? (ratings.reduce((s, n) => s + n, 0) / ratings.length) : null;
        const sentiment = avg == null
          ? "Mixed"
          : avg >= 4.5 ? "Glowing — overwhelmingly positive"
          : avg >= 4.0 ? "Strong positive — most guests highly satisfied"
          : avg >= 3.5 ? "Mostly positive with some friction"
          : avg >= 3.0 ? "Mixed — sentiment is split"
          : "Concerning — recurring complaints";
        return (
          <div className="hv-card p-4" data-testid="research-reviews">
            <h3 className="font-display text-base font-bold mb-3 flex items-center gap-2 flex-wrap">
              <Star className="w-4 h-4 text-amber-300 shrink-0" />
              <span>Google reviews</span>
              {avg != null && (
                <span className="text-xs font-normal text-[#dde3ec]">
                  · avg <span className="text-amber-300 font-mono">{avg.toFixed(1)}★</span> across {brief.reviews.length} review{brief.reviews.length === 1 ? "" : "s"}
                </span>
              )}
            </h3>

            {/* Sentiment summary */}
            <div className="mb-3 px-3 py-2 rounded-md border border-amber-500/25 bg-amber-500/5 text-xs text-[#dde3ec]" data-testid="research-reviews-summary">
              <span className="text-[10px] uppercase tracking-wider text-amber-300 font-bold mr-2">Sentiment</span>
              {sentiment}
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              {best && (
                <div className="border-l-2 border-emerald-500/50 pl-3" data-testid="research-review-best">
                  <div className="flex items-center gap-2 text-xs text-[#a0aabf] flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">Best</span>
                    <span className="font-bold text-white truncate">{best.author || "Anonymous"}</span>
                    <StarBar rating={best.rating} count={null} />
                  </div>
                  <p className="text-sm text-[#d4dbe7] mt-1 leading-relaxed break-words">{best.text}</p>
                </div>
              )}
              {worst && worst !== best && (
                <div className="border-l-2 border-rose-500/50 pl-3" data-testid="research-review-worst">
                  <div className="flex items-center gap-2 text-xs text-[#a0aabf] flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider text-rose-300 font-bold">Worst</span>
                    <span className="font-bold text-white truncate">{worst.author || "Anonymous"}</span>
                    <StarBar rating={worst.rating} count={null} />
                  </div>
                  <p className="text-sm text-[#d4dbe7] mt-1 leading-relaxed break-words">{worst.text}</p>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Internal intel */}
      <div className="hv-card p-4" data-testid="research-internal-intel">
        <h3 className="font-display text-base font-bold mb-3 flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-[#1fb6c1]" />
          Internal intel ({brief.internal_summary?.calls || 0} calls · {brief.internal_summary?.comments || 0} comments · {brief.internal_summary?.jobcards || 0} jobs · {brief.internal_summary?.gallery_docs || 0} docs)
        </h3>
        <div className="grid md:grid-cols-2 gap-3 text-xs">
          {brief.internal?.calls?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1.5">Call history</div>
              <ul className="space-y-1">
                {brief.internal.calls.slice(0, 6).map((c, i) => (
                  <li key={i} className="flex gap-2">
                    <Phone className="w-3 h-3 mt-0.5 text-[#1fb6c1] shrink-0" />
                    <span className="text-[#a0aabf]">
                      <span className="font-mono text-[10px]">{c.created_at?.slice(0, 10)}</span> {c.type}/{c.direction}: {c.notes?.slice(0, 120)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {brief.internal?.comments?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1.5">Comments</div>
              <ul className="space-y-1">
                {brief.internal.comments.slice(0, 6).map((c, i) => (
                  <li key={i} className="flex gap-2">
                    <FileText className="w-3 h-3 mt-0.5 text-[#A78BFA] shrink-0" />
                    <span className="text-[#a0aabf]">
                      <span className="font-bold text-white">{c.author_name}</span>: {c.text?.slice(0, 160)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {brief.internal?.jobcards?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1.5">Prior jobs</div>
              <ul className="space-y-1">
                {brief.internal.jobcards.slice(0, 5).map((j, i) => (
                  <li key={i} className="flex gap-2">
                    <CheckCircle2 className="w-3 h-3 mt-0.5 text-emerald-300 shrink-0" />
                    <Link to={`/jobcards/${j.id}`} className="text-[#a0aabf] hover:text-[#1fb6c1]">
                      #{j.short_id} [{j.phase}] {j.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {brief.website_scrape?.pages?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1.5">Website pages scraped</div>
              <ul className="space-y-1">
                {brief.website_scrape.pages.map((u, i) => (
                  <li key={i} className="flex gap-2">
                    <Globe className="w-3 h-3 mt-0.5 text-[#1fb6c1] shrink-0" />
                    <a href={u} target="_blank" rel="noreferrer" className="text-[#a0aabf] hover:text-[#1fb6c1] break-all">{u}</a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {!brief.internal?.calls?.length && !brief.internal?.comments?.length && !brief.internal?.jobcards?.length && (
          <p className="text-xs text-[#647087]">No prior interactions yet — this is a cold prospect.</p>
        )}
      </div>

      {/* Primary action footer */}
      <div className="hv-card p-4 flex items-center justify-between gap-3 flex-wrap border-[#1fb6c1]/30">
        <div className="text-xs text-[#a0aabf]">Ready to action this?</div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={async () => {
              // Wave 19 #101 — server-side Playwright PDF download.
              // Falls back to in-browser print if the endpoint fails or
              // takes too long (cold start / pod restart).
              setDownloadingPdf(true);
              const ctrl = new AbortController();
              const timer = setTimeout(() => ctrl.abort(), 30_000);
              try {
                const res = await api.get(
                  `/research/prospect/${id}/pdf`,
                  { responseType: "blob", signal: ctrl.signal, timeout: 30_000 },
                );
                const blob = new Blob([res.data], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `Brief_${(brief?.prospect_name || id).replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 64)}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 4000);
              } catch (e) {
                console.warn("Server PDF failed, falling back to print:", e);
                printInLightMode();
              } finally {
                clearTimeout(timer);
                setDownloadingPdf(false);
              }
            }}
            disabled={downloadingPdf}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="research-download-pdf"
            title="Download a server-rendered PDF — always light mode, deterministic layout"
          >
            <Download className="w-3 h-3" /> {downloadingPdf ? "Generating…" : "Download PDF"}
          </button>
          <button
            type="button"
            onClick={() => printInLightMode()}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="research-print"
            title="Print in browser — always renders in light mode"
          >
            <Printer className="w-3 h-3" /> Print
          </button>
          <button
            type="button"
            onClick={saveToCustomerDocs}
            disabled={savingDoc}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="research-save-doc"
            title="Save this brief to the customer's internal documents (NOT visible in the client portal)"
          >
            <Save className="w-3 h-3 text-[#A78BFA]" /> {savingDoc ? "Saving…" : savedDocId ? "✓ Saved" : "Save to customer docs"}
          </button>
          <Link to={`/leads/${id}`} className="hv-btn hv-btn-ghost text-xs" data-testid="research-action-lead">
            <FileText className="w-3 h-3" /> Open lead
          </Link>
          <Link to={`/trips`} className="hv-btn hv-btn-ghost text-xs" data-testid="research-action-trip">
            <MapPin className="w-3 h-3" /> Plan a trip
          </Link>
          {phone && (
            <a href={`tel:${phone}`} className="hv-btn hv-btn-primary text-xs" data-testid="research-action-call">
              <Phone className="w-3 h-3" /> Call now
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// Wave 25.17 — Free Leaflet mini-map for the research page (was a
// Google Maps Embed iframe). Uses OSM tiles with a satellite toggle.
function ResearchMiniMap({ lat, lng, label }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    const { map } = createMap(ref.current, { center: [lat, lng], zoom: 15 });
    mapRef.current = map;
    const m = L.marker([lat, lng], { icon: tealPin() }).addTo(map);
    if (label) m.bindTooltip(label, { direction: "top", offset: [0, -28] });
    return () => {
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch (_e) { /* ignore */ }
        mapRef.current = null;
      }
    };
  }, [lat, lng, label]);
  return <div ref={ref} className="w-full h-full" data-testid="research-map-canvas" />;
}

